import { useAuthStore } from '../../../store/auth';
import { refreshAccessToken } from './auth';


// Helper function to get headers with authentication
export function getAuthHeaders() {
  const { accessToken } = useAuthStore.getState();

  if (!accessToken) {
    throw new Error('Authentication token not found. Please log in.');
  }

  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
}

// Helper function to check if token is valid
export function isTokenValid(): boolean {
  const { isAuthenticated, checkTokenExpiry } = useAuthStore.getState();

  if (!isAuthenticated) {
    return false;
  }

  return checkTokenExpiry();
}

// Base fetcher function with authentication and token refresh
export async function apiFetch<T>(
  url: string,
  options: RequestInit = {},
  retryOnAuth = true,
): Promise<T> {
  try {
    // Add auth headers if not provided
    if (!options.headers) {
      options.headers = getAuthHeaders();
    }

    // Check token validity
    if (!isTokenValid() && retryOnAuth) {
      // Try to refresh the token
      const refreshed = await refreshAccessToken();

      if (!refreshed) {
        throw new Error('Session expired. Please log in again.');
      }

      // Update headers with new token
      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${useAuthStore.getState().accessToken}`,
      };
    }

    const response = await fetch(url, options);

    if (response.status === 401 && retryOnAuth) {
      // If unauthorized and we haven't tried refreshing yet
      const refreshed = await refreshAccessToken();

      if (refreshed) {
        // Retry the request with new token
        return apiFetch(
          url,
          {
            ...options,
            headers: {
              ...options.headers,
              Authorization: `Bearer ${useAuthStore.getState().accessToken}`,
            },
          },
          false,
        ); // Don't retry again to prevent infinite loop
      }

      // If we can't refresh, force logout
      useAuthStore.getState().clearTokens();
      throw new Error('Your session has expired. Please log in again.');
    }

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Request failed');
    }

    return response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Unknown error occurred');
  }
}
